// Constantes de domínio do módulo Fee Fixo.
// Centralizadas aqui para conformidade com o design-system.

import { toMoney, toPercent } from '@utils/Format'

// Fonte única de título/subtítulo por aba — consumida pelo fee-fixo-nav-bar
// (route-header) e por cada view via ConditionalTeleport (:routes), mesmo
// padrão do crmRoutes() em @modules/crm/constants/Text.
//
// ★ `fee-fixo.roleplay` entra SEMPRE na lista (desde 05/08/2026 — não
// reintroduza `if (temIaFeeFixo)` ao redor dela; quem decide o placeholder é
// a VIEW, não esta lista). Sem parâmetro de propósito, para não reintroduzir
// o gate por engano.
export const feeFixoRoutes = () => [
  {
    // ★ Entrada em conversa (spec-001 do project-onboarding-with-ai-fee-fixo,
    // ADR-003). PRIMEIRA da lista de propósito: com a flag `IA - Fee Fixo` é
    // para cá que `/fee-fixo` redireciona.
    //
    // Como o resto da lista, entra SEM gate — quem filtra é o
    // `fee-fixo-nav-bar.vue` (computed `routes()`), no mesmo lugar em que a
    // aba de Configurações some para não-gestor. A aba some sem a flag (e a
    // rota devolve 404): diferente do Ensaio, o guia não é vitrine — não há
    // CTA de conversão numa conversa que o backend recusa.
    name: 'fee-fixo.guia',
    title: 'Guia',
    subtitle:
      'Converse com o guia da AAWZ e siga o próximo passo da sua migração para Fee Fixo.',
  },
  {
    name: 'fee-fixo.welcome',
    title: 'Bem-vindo',
    subtitle: 'Conheça o módulo de Migração Fee Fixo da AAWZ',
  },
  {
    name: 'fee-fixo.dashboard',
    title: 'Visão Geral',
    subtitle: 'Acompanhe o cenário de migração para Fee Fixo da sua carteira.',
  },
  {
    name: 'fee-fixo.clientes',
    title: 'Clientes',
    subtitle:
      'Explore os clientes da sua carteira e simule a migração para Fee Fixo.',
  },
  {
    name: 'fee-fixo.simulacao',
    title: 'Simulação',
    subtitle:
      'Compare cenários de custo e receita na jornada de migração para Fee Fixo.',
  },
  {
    name: 'fee-fixo.pipeline',
    title: 'Acompanhamento',
    subtitle:
      'Acompanhe o avanço de cada cliente na jornada de migração para Fee Fixo.',
  },
  {
    name: 'fee-fixo.configuracoes',
    title: 'Configurações',
    subtitle:
      'Premissas do escritório usadas na simulação, nos relatórios em PDF e no assistente de IA — cenários já salvos não mudam.',
  },
  {
    name: 'fee-fixo.roleplay',
    title: 'Ensaio da conversa de migração',
    subtitle:
      'Você fala, o cliente simulado responde com os números do cenário dele, e no fim o ensaio recebe uma nota por bloco do gabarito.',
    // A rota virou shell (redirect + children, ver routes.js) — sem este
    // array, o NavBarNavigation (isSelected()) só destacaria a aba quando
    // $route.name fosse EXATAMENTE 'fee-fixo.roleplay', que nunca acontece
    // de verdade (a rota só existe como redirect). Mesmo padrão de
    // compliance/constants/Text.js:24-28.
    children: ['fee-fixo.roleplay.ensaio', 'fee-fixo.roleplay.historico'],
  },
]

export const VIEW_MODE_OPTIONS = [
  { text: 'Minha Carteira', value: 'profissional' },
  { text: 'Escritório', value: 'escritorio' },
]

export const STATUS_LABELS = {
  elegivel: 'Elegível',
  em_potencial: 'Em Potencial',
  nao_elegivel: 'Não Elegível',
  migracao_parcial: 'Migração Parcial',
}

export const STATUS_COLORS = {
  elegivel: '#56AF79',
  em_potencial: '#F39C12',
  nao_elegivel: '#E74C3C',
  migracao_parcial: '#3498DB',
}

export const CENARIOS_CAPTACAO = [
  { text: 'Conservador', value: 'conservador' },
  { text: 'Base', value: 'base' },
  { text: 'Otimista', value: 'otimista' },
  { text: 'Misto', value: 'misto' },
]

export const TIPOS_ATUACAO = [
  { text: 'Assessor', value: 'assessor' },
  { text: 'Consultor CVM', value: 'consultor_cvm' },
]

// ★ NÃO existe piso de PL na BASE deste módulo (05/08/2026). Havia um
// `PL_MINIMO_ELEGIVEL` de R$ 1 milhão que descartava o cliente da listagem, do
// Dashboard e do denominador dos percentuais — e o descarte era MUDO: cliente
// importado com sucesso (o caso relatado tinha R$ 720 mil) simplesmente não
// aparecia, com a tela dizendo "nenhum cliente elegível". A base das três abas
// (o computed `clientesElegiveis`) continua a carteira INTEIRA da visão, sem
// corte embutido — isso não voltou.
//
// O que existe desde 10/08/2026 é diferente: o filtro "Faixa de PL" da aba
// Clientes (ver `PL_ACIMA_DE_PADRAO` abaixo) nasce com um valor JÁ APLICADO.
// Não é o defeito antigo — o consultor VÊ o campo preenchido, é a mesma badge
// de "filtros ativos" de qualquer outro filtro, e ele limpa/ajusta em dois
// cliques. A diferença do `PL_MINIMO_ELEGIVEL` de antes é exatamente essa:
// aquele era mudo e não estava nos filtros; este é um filtro comum com valor
// inicial.

// Valor inicial do filtro "Patrimônio acima de" na aba Clientes (10/08/2026).
// Só pré-preenche o campo "acima de"; "abaixo de" nasce sem teto. Ver
// `view-fee-fixo-clientes.vue`, `filtroPlAcimaDe`.
export const PL_ACIMA_DE_PADRAO = 300000

// --- Colunas de custo do cliente e receita do assessor (view Clientes) ---
// A unidade vai no NOME do campo e no cabeçalho: `R.cost`/`R.adv` do engine
// estão em bps (cost.comis = 117,128), enquanto `accComis`/`patrGain` estão em
// reais. A spec-001 quebrou por confundir os dois.
//
// ★ A coluna é o campo `_reais` (R$/ano), não o `_bps`: com as premissas padrão
// os bps não dependem do AuC, então exibir/ordenar por bps repetiria o mesmo
// número em todas as linhas da carteira — a coluna não responderia "quanto ESTE
// cliente paga", que é a pergunta que ela existe para responder. O `_bps` segue
// na linha como a taxa que originou o valor, exibida abaixo dele.
//
// `chave` é o campo do rótulo pré-computado na linha (`simulacao_texto`): a
// célula não pode chamar função no template, então o texto já vem pronto.
export const COLUNAS_SIMULACAO = [
  {
    value: 'custo_cliente_comissionado_reais',
    valueBps: 'custo_cliente_comissionado_bps',
    chave: 'custo_comissionado',
    text: 'Custo comissionado (R$/ano)',
  },
  {
    value: 'custo_cliente_fee_reais',
    valueBps: 'custo_cliente_fee_bps',
    chave: 'custo_fee',
    text: 'Custo fee (R$/ano)',
  },
  {
    value: 'receita_assessor_comissionado_reais',
    valueBps: 'receita_assessor_comissionado_bps',
    chave: 'receita_comissionado',
    text: 'Receita comissionado (R$/ano)',
  },
  {
    value: 'receita_assessor_fee_reais',
    valueBps: 'receita_assessor_fee_bps',
    chave: 'receita_fee',
    text: 'Receita fee (R$/ano)',
  },
]

// Toggle único: `false` esconde as 4 colunas acima da tabela de Clientes da
// Carteira sem apagar a definição. Trocar pra `true` religa as 4 na hora, sem
// mexer em mais nenhum arquivo (view-fee-fixo-clientes.vue consome só a
// versão "visível" abaixo, nunca `COLUNAS_SIMULACAO` direto).
export const EXIBIR_COLUNAS_SIMULACAO = false

export const COLUNAS_SIMULACAO_VISIVEIS = EXIBIR_COLUNAS_SIMULACAO
  ? COLUNAS_SIMULACAO
  : []

// Honestidade do número: as colunas são estimativa a partir do AuC com as
// premissas padrão, não a conta da carteira real do cliente. Quem decide com
// base no número precisa saber de onde ele veio — inclusive que a taxa (bps) é
// a mesma para toda a carteira e é só o AuC que muda o valor.
export const TOOLTIP_ESTIMATIVA_POR_AUC =
  'Valor em R$/ano estimado a partir do AuC do cliente, com as premissas padrão da simulação — não é a carteira real. Abaixo do valor vem a taxa que o originou (bps): nas premissas padrão ela é igual para toda a carteira, e o que muda de cliente para cliente é o AuC sobre o qual ela incide.'

// Texto de PRODUTO para a procedência do custo composto por ativo
// (`custoOrigem.fonte`, `FeeLabAlocacaoOrigem`). O `fonte` que o backend manda
// (`aawz_fee_fixo/core/custo_por_ativo.py:FONTE_CUSTO`) é copy de AUDITORIA,
// não de produto: nomeia a coluna do banco onde o dado é lido
// (`app_dados_ativos_clientes.custo_estimado_bps`) — informação que ajuda
// quem investiga o dado, não o consultor lendo um tooltip na frente do
// cliente. As DUAS metades que importam para o consultor (é estimativa, não
// é apurado contra a CVM) ficam aqui; o nome da tabela/coluna nunca chega à
// tela.
export const TOOLTIP_CUSTO_ORIGEM_FONTE =
  'Estimativa do extrator de documentos, não verificada na CVM.'

// Rótulos PT-BR por FONTE do custo composto (spec-005 do
// `cnpj-por-nome-e-custo-cvm-com-fonte-persistida`, [T006] — US-4). As sete
// chaves espelham `custo_por_ativo.FONTES_CUSTO` do backend (a ordem também —
// o tooltip lista nesta ordem, da fonte mais verificada à menos). Nunca a
// chave crua na tela: `cvm`/`cvm_cascata`/etc. é vocabulário de banco, não de
// produto — mesma razão de `TOOLTIP_CUSTO_ORIGEM_FONTE` acima.
export const FONTES_CUSTO_ORIGEM = {
  cvm: 'verificadas na CVM (taxa de administração)',
  cvm_cascata:
    'custo total look-through (taxa própria + fundos investidos, cobertura ≥ 20%)',
  emissor: 'pelo emissor (tabela empírica)',
  tipo: 'pela regra do tipo',
  classe_anbima: 'média da classe ANBIMA',
  modelo: 'estimadas pelo extrator (não verificadas)',
  informado: 'informadas manualmente',
}

// --- Status de migração (régua canônica) ---
// Os três valores que `usecase_fee_fixo_clientes.py:240-247` serializa em
// `status_migracao`: `fee_fixo_ativo` OU oportunidade na etapa Fechamento →
// migrado; etapa intermediária → em migração; senão → não iniciado.
//
// ★ Ficam nomeados aqui porque DUAS telas os consomem — o filtro da listagem de
// clientes e os KPIs do Acompanhamento (spec-015) — e o literal solto em cada
// uma diverge na primeira mudança de régua, fazendo as duas abas darem números
// diferentes para a mesma pergunta.
export const STATUS_MIGRACAO = {
  FEE_FIXO: 'fee_fixo',
  EM_MIGRACAO: 'em_migracao',
  NAO_INICIADO: 'nao_iniciado',
}

// --- Filtros da listagem de clientes (view Clientes) ---
// O filtro "Status" da view Clientes NÃO usa mais esta lista fixa: as opções
// são derivadas do status granular (statusChip.label) dos clientes carregados
// — ver statusFilterOptions em view-fee-fixo-clientes.vue. STATUS_MIGRACAO_OPTIONS
// segue exportado porque outras telas (KPIs de migração do Acompanhamento)
// ainda usam os 3 buckets macro como lookup de rótulo.
export const STATUS_MIGRACAO_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Em Fee Fixo', value: STATUS_MIGRACAO.FEE_FIXO },
  { text: 'Em Migração', value: STATUS_MIGRACAO.EM_MIGRACAO },
  { text: 'Não Iniciado', value: STATUS_MIGRACAO.NAO_INICIADO },
]

export const FAIXA_ETARIA_OPTIONS = [
  { text: 'Todas as idades', value: null },
  { text: '18 a 30 anos', value: '18-30' },
  { text: '30 a 50 anos', value: '30-50' },
  { text: '50 a 65 anos', value: '50-65' },
  { text: 'Acima de 65 anos', value: '65+' },
]

export const SEGMENTACAO_OPTIONS = [
  { text: 'Todas', value: null },
  { text: 'Varejo', value: 'Varejo' },
  { text: 'Alta Renda', value: 'Alta Renda' },
  { text: 'Private', value: 'Private' },
  { text: 'Ultra High', value: 'Ultra High' },
]

// Label + cor por status de migração (usado na tabela de clientes).
export const STATUS_MIGRACAO_CONFIG = {
  fee_fixo: { label: 'Em Fee Fixo', color: '#128e45' },
  em_migracao: { label: 'Em Migração', color: '#FB8C00' },
  nao_iniciado: { label: 'Não Iniciado', color: '#7C89B7' },
}

// Cor neutra de fallback (BaseStatus exige hex — não aceita var(--token), ver
// formatColor() em _base-status.vue) — token equivalente: neutralScale.900 /
// --secondary (design-system/tokens.json). Única fonte para não duplicar o
// literal em múltiplos arquivos.
export const COR_FALLBACK_STATUS = '#8E8E8E'

// Tier de prioridade do cliente na migração — régua ÚNICA das duas telas desde
// a spec-013 [T005]: Pareto da receita fee estimada sobre a carteira da visão.
// A: os 20% de maior receita · B: os 30% seguintes · C: os 50% restantes.
//
// ★ `description` descrevia a régua win-win ("Migrar para Fee") enquanto o
// Dashboard a usava e a listagem já estava no Pareto. Agora as duas telas leem
// daqui e a frase descreve a régua vigente: o Pareto ORDENA por receita, não
// decide destino de regime — prometer "Migrar para Fee" no rótulo do tier era
// afirmar uma recomendação que esta régua não faz. Quem responde "a migração
// fecha para os dois lados?" é o indicador Win-win (`WIN_WIN_CONFIG`), que tem
// nome próprio na listagem exatamente para não voltar a ser confundido com o
// Tier.
export const TIER_CONFIG = {
  A: {
    label: 'Tier A',
    color: '#128e45',
    description: 'Top 20% em receita fee estimada',
  },
  B: {
    label: 'Tier B',
    color: '#FB8C00',
    description: 'Os 30% seguintes em receita',
  },
  C: {
    label: 'Tier C',
    color: '#E74C3C',
    description: 'Os 50% restantes em receita',
  },
}

// Opções do filtro de Tier da listagem — texto na régua de Pareto (spec-006).
export const TIER_OPTIONS = [
  { text: 'Todos', value: null },
  { text: 'Tier A (top 20% em receita)', value: 'A' },
  { text: 'Tier B (30% seguintes)', value: 'B' },
  { text: 'Tier C (50% restantes)', value: 'C' },
]

// Cliente que não apareceu no mapa de tiers cai na cauda: nunca inventa
// prioridade que o Pareto não deu.
export const TIER_PADRAO = 'C'

// --- Indicador win-win (independente do Tier) ---
// Win-win = a migração aumenta o patrimônio do cliente E a receita do assessor
// (`patrGain.fee > 0 && advGain.fee > 0`). Era o critério do Tier antes do
// Pareto; virou indicador próprio porque responde outra pergunta — "a migração
// compensa para os dois lados?" não é o mesmo que "quanto este cliente rende".
//
// O caso que ele existe para mostrar é o Tier A SEM win-win: o cliente que mais
// renderia e para quem a conta não fecha. Sem o indicador, o tier alto o
// esconderia.
//
// `indefinido`: sem AuC não há base para calcular. Ausência não é resposta
// negativa — não afirmamos que a migração não compensa.
// ★ `cor` é nome de token que o icon-svg resolve para `var(--token)`: só cabem
// tokens exportados como CSS custom property em `src/design/_vars.sass`
// ($colors / $gray-colors / $theme-colors). `warning-new` existe apenas como
// SASS — `var(--warning-new)` não resolve e o ícone perderia a cor sem erro
// nenhum. O amarelo exportado é `await`.
export const WIN_WIN_CONFIG = {
  sim: {
    icone: 'check-circle',
    cor: 'success',
    texto:
      'Win-win: na migração o cliente ganha patrimônio e o assessor ganha receita.',
  },
  nao: {
    // `warning-status`, não `warning-circle`: este último desenha outra coisa (é
    // o `ChartBarIconWarning`, um avião de papel num anel) e traz `fill="#231F20"`
    // e `fill="white"` fixos, que sobrescrevem o token e deixam o ícone com a cor
    // errada. `warning-status` é o canônico de aviso — controladoria, solicitações
    // e billing usam ele. Travado por `[D-4]` em `view-fee-fixo-clientes.unit.js`.
    icone: 'warning-status',
    cor: 'await',
    texto:
      'Não é win-win: a migração não fecha para os dois lados — revise antes de propor.',
  },
  indefinido: {
    icone: '',
    cor: '',
    texto: 'Sem AuC para estimar a migração.',
  },
}

// ★ A segunda frase não é rodapé: com as premissas padrão o indicador dá "sim"
// para 100% da base — `advGain.fee` é constante e `patrGain.fee` é proporcional
// ao AuC, então o sinal nunca vira. (É também o motivo de o Tier antigo, que
// usava essa condição, classificar a carteira inteira como A.) Sem o aviso,
// quem abre a tela vê a coluna toda verde e conclui que está quebrada.
export const TOOLTIP_WIN_WIN =
  'Mostra se a migração compensa para os dois lados ao mesmo tempo: patrimônio do cliente e receita do assessor. É independente do Tier — um cliente Tier A pode não ser win-win. Com as premissas padrão de hoje a resposta é "sim" para toda a base; a coluna passa a separar clientes quando entrarem as premissas do escritório e a carteira real.'

// A régua do Tier mudou (era win-win, virou Pareto de receita) e o mesmo cliente
// pode ser A em "Minha Carteira" e C em "Escritório". Sem este aviso, quem já
// usava a tela lê o A novo com o significado velho.
export const TOOLTIP_TIER_PARETO =
  'Prioridade por Pareto da receita fee estimada: os 20% de maior receita são Tier A, os 30% seguintes B e os 50% restantes C. O tier é relativo à carteira exibida — o mesmo cliente pode ser A em "Minha Carteira" e C em "Escritório".'

// Rodapé do filtro de Tier: dimensiona o esforço da migração. Texto curto de
// propósito — mora na barra de filtros, ao lado de um select de 180px.
export const SEM_BASE_PARA_PRIORIZAR = 'Sem base para priorizar.'

export function montarResumoConcentracaoTierA(percentual) {
  return `Tier A concentra ${percentual} da receita fee.`
}

// Premissas padrão da calculadora (seção 7 do REQUIREMENTS).
export const CALCULADORA_DEFAULTS = {
  patrimonio_total: 0,
  patrimonio_fora: 0,
  roa: 0.35,
  comissao: 45,
  imposto: 19.9,
  fee: 0.7,
  taxa_plataforma: 0.15,
  cenario_captacao: 'base',
  tipo_atuacao: 'assessor',
}

// --- Status derivado (etapa do funil / desfecho) ---
// Espelho de `aawz-web-backend/src/aawz_fee_fixo/constants.py` — alterar os dois juntos.
// Usado como fallback/otimista quando o payload traz etapa_nome/etapa_cor/desfecho
// (coluna Status da tabela de Clientes e sincronia otimista do Pipeline).
export const STATUS_DERIVADO_CONFIG = {
  em_fee_fixo: { nome: 'Em Fee Fixo', cor: '#128e45' },
  ganho: { nome: 'Ganho', cor: '#128e45' },
  perdido: { nome: 'Perdido', cor: '#E74C3C' },
  nao_iniciado: { nome: 'Não Iniciado', cor: '#7C89B7' },
}

export const DESFECHO = {
  GANHO: 'ganho',
  PERDIDO: 'perdido',
  ANDAMENTO: 'andamento',
}

// Tipo de oportunidade do funil CRM usado para a Migração Fee Fixo — compara
// contra `funil.tipo_oportunidade` / `payload.tipoOportunidade`. Compartilhado
// entre a view do Pipeline e o handler global de desfecho no shell do módulo
// (fee-fixo-nav-bar.vue), então precisa viver fora de ambos.
export const TIPO_OPORTUNIDADE_FEE_FIXO = 'Migração Fee Fixo'

// --- Patrimônio por etapa (spec-015, [US-1]) ---
// ★ A legenda existe porque o número da coluna e o dos cards de PL NÃO são a
// mesma coisa e vão divergir na tela: a etapa soma o `valor` da oportunidade
// (AuC congelado na criação, `usecase_fee_fixo_oportunidade_pipeline.py:332`) e
// o card soma o PL corrente do cliente. Sem dizer isso, quem vê R$ 3 mi numa
// coluna e R$ 8 mi no card conclui que um dos dois está quebrado.
//
// A segunda frase cobre o outro desencontro visível: a coluna mostra os cards
// já ganhos/perdidos, mas eles não entram na soma — mesma convenção do card
// "Patrimônio Total", que também conta só as abertas.
// Rótulos dos três blocos de KPI da migração (spec-015, [US-2]). O bloco de PL
// diz "corrente" no título porque a coluna do Kanban, logo abaixo, mostra a
// outra fonte — sem a palavra, os dois números parecem a mesma medida.
//
// O singular/plural muda por bloco ("Migrados" clientes, "Migrado" PL,
// "Migrada" receita): é o mesmo conceito, mas concordando com o substantivo de
// cada bloco.
export const KPIS_MIGRACAO_LABELS = {
  clientes: {
    titulo: 'Clientes',
    migrado: 'Migrados',
    andamento: 'Em andamento',
  },
  pl: {
    titulo: 'PL corrente',
    migrado: 'Migrado',
    andamento: 'Em andamento',
  },
  receita: {
    titulo: 'Receita fee estimada (R$/ano)',
    migrado: 'Migrada',
    andamento: 'Em andamento',
    total: 'Total',
  },
}

// O util lança de propósito (campo em bps, premissa inválida). A mensagem diz a
// próxima ação — erro sem saída deixa o usuário parado.
export const ERRO_KPIS_MIGRACAO =
  'Não foi possível calcular os números da migração. Atualize a página e, se ' +
  'o problema continuar, contate o suporte.'

export const LEGENDA_PATRIMONIO_ETAPA =
  'Patrimônio por etapa: soma do valor da oportunidade — o AuC registrado ' +
  'quando ela foi criada, que não acompanha o PL corrente do cliente. ' +
  'Oportunidades já ganhas ou perdidas seguem visíveis na coluna, mas não somam.'

// --- Identificação do cliente na UI ---
// Na base importada o `nome_cliente` costuma ser o próprio código. Repetir o
// mesmo valor ("2092367 · 2092367") não identifica melhor o cliente, então
// nome-igual-ao-código conta como ausência de nome. Fonte única da regra:
// cabeçalho do painel do Especialista e pergunta do racional consomem daqui.
export function nomeUtil({ nome, codigo } = {}) {
  if (!nome || nome === codigo) return ''
  return nome
}

/**
 * O rótulo do cliente na lateral do guia e na abertura da conversa —
 * `000124 · Ana Prado`, ou só o código quando o nome não identifica ninguém.
 *
 * Mesma régua do `nomeUtil` acima, aplicada uma vez: sem ela a base importada
 * produzia `000125 · 000125` (medido pelo QA em 23/08/2026, G-Q4). Sem código
 * não há rótulo — uma etiqueta só com o nome não diz de qual cadastro é.
 *
 * @param {Object=} cliente `{ codigo, nome }`
 * @returns {string} Rótulo, ou '' quando não há como identificar.
 */
export function rotuloDoCliente({ codigo, nome } = {}) {
  const cod = String(codigo || '').trim()
  if (!cod) return ''
  const identificado = nomeUtil({ nome, codigo: cod })
  return identificado ? `${cod} · ${identificado}` : cod
}

// --- Ação "Racional" da tabela de clientes ---
// Pergunta pronta que a ação da linha manda para o Especialista Fee-Based.
// Identificar o cliente por nome E código é o que faz o agente chamar a tool do
// racional em vez de responder de memória; sem nome, o código sozinho resolve.
export function montarPerguntaRacional({ nome, codigo } = {}) {
  const identificado = nomeUtil({ nome, codigo })
  const alvo = identificado
    ? `${identificado} (código ${codigo})`
    : `de código ${codigo}`
  return `Monte o racional de migração do cliente ${alvo} e me ajude a preparar a conversa.`
}

// --- Gráficos do recorte filtrado (spec-013) ---
// Rótulos e avisos de premissa do card de gráficos da aba Clientes.
//
// ★ Este bloco é CONTRATO COMPARTILHADO com a spec-015: "% da base" e
// "estimativa" precisam significar a mesma coisa nas duas abas do módulo. Por
// isso o texto mora aqui e é IMPORTADO — copiar a string para o componente é o
// defeito que este bloco existe para impedir, e o `.unit.js` de cada consumidor
// assere identidade com a constante, não igualdade de texto.

export const LABEL_RECEITA_ESTIMADA = 'Receita estimada (R$/ano)'
export const LABEL_RECEITA_COMISSAO = 'Comissão hoje'
export const LABEL_RECEITA_FEE = 'Fee estimado'
export const LABEL_PL_RECORTE = 'PL do recorte'
export const LABEL_PCT_CLIENTES = 'Clientes'
export const LABEL_PCT_PL = 'PL'
// Cabeçalho da coluna de barras (% Clientes + % PL): "participação" cobre as
// duas medidas — "distribuição de PL" descreveria só uma das duas barras.
export const LABEL_PARTICIPACAO_RECORTE = 'Participação do recorte'

// Sem denominador não há percentual — a tela exibe o travessão. "0%" seria
// medição ("nenhum da carteira"), e não é isso que aconteceu.
export const SEM_DENOMINADOR = '—'

// Os dois estados vazios são diferentes e a mensagem precisa dizer qual é: sem
// cliente na visão o consultor não tem o que filtrar; com clientes, foi o
// filtro dele que não selecionou ninguém.
//
// ★ A mensagem da visão vazia NÃO fala em elegibilidade desde 05/08/2026: não
// há mais piso de PL, então "nenhum cliente elegível" mandava o consultor
// procurar um critério que não existe — enquanto o que faltava era importar a
// base (ou trocar de visão).
export const SEM_CLIENTES_NO_FILTRO =
  'Nenhum cliente atende aos filtros aplicados. Ajuste ou limpe os filtros para ver o tamanho do recorte.'
export const SEM_CLIENTES_NA_VISAO =
  'Nenhum cliente nesta visão. Importe a base de clientes ou troque de visão.'
// ★ [G-13] STRESS rodada 1 (07/09/2026): gestor comumente não tem vínculo
// direto em `app_profissional_clientes` — "Minha Carteira" abre vazia bem na
// PORTA DE ENTRADA do módulo, e a frase genérica acima ("troque de visão")
// não diz COMO. Esta mensagem só aparece junto do atalho que já troca a
// visão (`view-fee-fixo-clientes.vue`'s `ofereceVisaoEscritorio`) — nunca
// sozinha, senão prometeria uma ação que a tela não oferece.
export const SEM_CLIENTES_NA_VISAO_GESTOR =
  'Você ainda não tem clientes vinculados diretamente à sua carteira. Veja a carteira do escritório.'
export const ERRO_AGREGADOS_RECORTE =
  'Não foi possível calcular os números do recorte. Atualize a página e, se o problema continuar, contate o suporte.'

// Taxas do domínio são frações anuais (0,007). O texto mostra "0,7% a.a.", sem
// zero à direita — `ignore_zero` evita o "0,70%" que ninguém escreve.
//
// ★ Já termina em ponto ("a.a."): quem fecha frase com esta função NÃO acrescenta
// outro, senão sai "0,35% a.a..".
const taxaAoAno = (fracao) => `${toPercent(fracao * 100, 2, true)} a.a.`

const plural = (quantos, singular, plural_) =>
  quantos === 1 ? singular : plural_

// ★ O denominador vai NO RÓTULO — sem ele o consultor compara dois recortes que
// não são comparáveis. O nome do conjunto passou a ser "a carteira" em
// 05/08/2026: com a retirada do piso de PL, o denominador é a visão inteira, e
// continuar dizendo "dos elegíveis" nomearia um subconjunto que não existe mais.
export function montarRotuloPctClientes(fracao) {
  if (typeof fracao !== 'number' || !isFinite(fracao)) return SEM_DENOMINADOR
  return `${toPercent(fracao * 100, 1)} da carteira`
}

export function montarRotuloPctPl(fracao) {
  if (typeof fracao !== 'number' || !isFinite(fracao)) return SEM_DENOMINADOR
  return `${toPercent(fracao * 100, 1)} do PL da carteira`
}

// A receita fee é `PL × taxa padrão` — projeção, não realizado. Dizer a taxa é
// o que permite ao consultor refazer a conta e discordar dela.
export function montarAvisoTaxaFeeEstimada(taxaFee) {
  return (
    `Receita fee estimada com a taxa padrão de ${taxaAoAno(
      taxaFee
    )} sobre o PL ` + 'do recorte — é projeção, não receita realizada.'
  )
}

// ★ O caso real da base é "todos sem ROA medido" (`clientes_partners.roa` é nulo
// em 785.211 registros de produção). Escrever "794 de 794" nesse cenário lê como
// bug de contagem; o texto diz o que de fato é — a receita de comissão exibida é
// estimativa para TODO o recorte. O número continua na frase, que é o que a US-3
// exige: contador, não adjetivo.
export function montarAvisoRoaPresumido({ semRoa, total, roaPadrao } = {}) {
  if (!semRoa) return ''

  const taxa = taxaAoAno(roaPadrao)
  if (semRoa >= total) {
    const quem =
      total === 1
        ? 'O único cliente do recorte não tem ROA medido'
        : `Nenhum dos ${total} clientes do recorte tem ROA medido`
    return (
      `${quem}: a receita de comissão exibida é estimativa para todo o ` +
      `recorte, calculada com o ROA padrão de ${taxa}`
    )
  }

  return (
    `${semRoa} de ${total} clientes do recorte não têm ROA medido: para eles, a ` +
    `receita de comissão é estimativa calculada com o ROA padrão de ${taxa}`
  )
}

// Sinaliza — não corrige. Usar a taxa contratada de cada cliente muda o número
// da tela inteira e é decisão de produto própria (spec-013 §9); aqui o consultor
// só precisa saber que, para esses clientes, a estimativa não é o contrato.
export function montarAvisoTaxaPropriaDiferente({ quantos, taxaFee } = {}) {
  if (!quantos) return ''

  const sujeito = plural(
    quantos,
    `${quantos} cliente do recorte contratou`,
    `${quantos} clientes do recorte contrataram`
  )
  const posse = plural(quantos, 'dele', 'deles')
  return (
    `${sujeito} taxa diferente de ${taxaAoAno(taxaFee)} — a estimativa acima ` +
    `não reflete o contrato ${posse}.`
  )
}

// --- Planejamento financeiro na jornada de migração (spec-011) ---
// A coluna tem TRÊS estados, e o terceiro é o ponto da spec: hoje nem todo
// cliente do Fee Fixo tem vínculo com `app_clientes`, e sem vínculo não há como
// saber se ele fez planejamento. Exibir isso como "não fez" é falso negativo
// numa coluna que decide a quem o consultor liga — ele refaria um planejamento
// que já existe, ou deixaria de usar um argumento que já tinha.
//
// Os três valores são os que `core/financial_planning_status.py` serializa.
export const PLANEJAMENTO_ESTADOS = {
  FEZ: 'fez',
  NAO_FEZ: 'nao_fez',
  INDETERMINADO: 'indeterminado',
}

// ★ `indeterminado` tem DUAS causas, e esta frase é a ÚNICA fonte da explicação
// na plataforma (o payload não traz motivo — `usecase_fee_fixo_clientes.py`
// serializa só o estado):
//
//   1. o cliente não tem `id_cliente_app` — o caso comum, um por linha;
//   2. a consulta às simulações falhou — e aí a COLUNA INTEIRA vem
//      indeterminada, num escritório onde todo mundo tem vínculo.
//
// Por isso a frase abre pela dúvida e cita o vínculo como causa PROVÁVEL. Dizer
// "este cliente não tem vínculo" seria mentira no caso 2, e é justamente o caso
// em que a tela repetiria a acusação 794 vezes.
export const MOTIVO_PLANEJAMENTO_INDETERMINADO =
  'Não foi possível saber se este cliente já fez planejamento financeiro. Em geral é porque falta o vínculo dele com o cadastro da plataforma; também pode ser falha temporária na consulta — nesse caso, atualize a página em alguns minutos.'

export const MOTIVO_SEM_VINCULO_LABORATORIO =
  'Sem vínculo com o cadastro da plataforma, não dá para abrir o laboratório com este cliente.'

export const MOTIVO_CLIENTE_FORA_DA_LISTA =
  'Não encontramos este cliente na lista da plataforma, então o laboratório abriria com o cliente errado. Atualize a página e tente de novo; se persistir, contate o suporte.'

// ★ O indeterminado é âmbar, não vermelho: ausência de vínculo não é erro do
// cliente nem falha do sistema — é dado que falta. Vermelho leria como "deu
// errado" e mandaria o consultor procurar problema onde não há.
export const PLANEJAMENTO_CONFIG = {
  [PLANEJAMENTO_ESTADOS.FEZ]: {
    label: 'Fez',
    color: '#128e45',
    motivo: '',
  },
  [PLANEJAMENTO_ESTADOS.NAO_FEZ]: {
    label: 'Não fez',
    color: '#7C89B7',
    motivo: '',
  },
  [PLANEJAMENTO_ESTADOS.INDETERMINADO]: {
    label: 'Sem informação',
    color: '#FB8C00',
    motivo: MOTIVO_PLANEJAMENTO_INDETERMINADO,
  },
}

// A coluna é uma leitura do OUTRO módulo: dizer isso no cabeçalho evita que o
// consultor a leia como etapa do funil de migração.
export const TOOLTIP_PLANEJAMENTO_FINANCEIRO =
  'Indica se o cliente já tem ao menos uma simulação no módulo de Planejamento Financeiro deste escritório. "Sem informação" significa que o cliente ainda não tem vínculo com o cadastro da plataforma — não que ele não tenha feito.'

export const ACAO_ABRIR_LABORATORIO = 'Abrir no Planejamento Financeiro'

// ★ O MESMO ícone com que a plataforma identifica o Financial Planning na
// navegação (`constants/navigation-routes.js:137`) — mesmo conceito, mesmo
// símbolo. O arquivo precisa existir em `components/icons/svg`: nome inexistente
// renderiza invisível, sem erro nenhum, e a ação some da tela.
export const ICONE_FINANCIAL_PLANNING = 'mdi-chart-timeline-variant'

// Dica do atalho de teclado dos DOIS campos de IA do módulo (este card e o
// Especialista Fee-Based, fee-fixo-ia-especialista.vue) — constante única
// pra não divergir entre os dois na primeira edição de um dos lados.
export const DICA_ATALHO_ENVIO_IA = 'Shift+Enter envia • Enter quebra linha'

// --- Filtro montado pela IA (spec-012, [US-2] [US-3]) ---
// A tradução do filtro tipado para português é COPY DA TELA, e é por isso que
// mora aqui e não no util: o módulo par no backend
// (`aawz_fee_fixo/core/filtro_clientes.py`) declara explicitamente que não
// escreve frase de tela — duas copies para a mesma coisa divergem na primeira
// edição. O que vem do util é a semântica (rótulo do campo e unidade do valor).

export const FILTRO_IA_LABELS = {
  titulo: 'Priorizar clientes com IA',
  descricao:
    'Descreva quem você quer priorizar — de critérios simples a filtros mais ' +
    'complexos, a IA monta o filtro e você confere antes de aplicar.',
  placeholder:
    'Ex.: clientes com mais de 3 milhões que ainda não começaram a migração',
  perguntar: 'Montar filtro',
  dica: DICA_ATALHO_ENVIO_IA,
  aplicar: 'Aplicar filtro',
  descartar: 'Descartar',
  desfazer: 'Desfazer',
  tituloSugestao: 'Filtro sugerido',
  aplicadoAgora:
    'Filtro aplicado. Ele está nos controles da tela e pode ser ajustado à mão.',
  // Os dois extremos avisam antes do clique: recorte vazio não tem o que
  // aplicar, e recorte que pega todo mundo quase nunca é o que se quis.
  nenhumCliente:
    'Este filtro não seleciona nenhum cliente. Reescreva o pedido com outros critérios.',
  todosOsClientes:
    'Este filtro seleciona todos os clientes da visão — provavelmente falta um critério.',
  // [US-5] "Ninguém tem esse dado" NÃO é "ninguém atende ao critério". A lista
  // vazia confunde as duas, e a diferença muda o que o consultor faz: uma pede
  // outro critério, a outra pede importar a carteira.
  classeIndisponivel:
    'Nenhum cliente desta visão tem a carteira classificada, então o filtro por ' +
    'classe de ativo ainda não está disponível — importe as carteiras para usá-lo.',
}

// A recusa é da tela para o consultor: ele não montou o filtro e não tem o que
// corrigir no objeto, então a frase diz o que aconteceu e o que fazer. Os
// motivos técnicos do util vão ABAIXO dela, para quem quiser ler.
export const ERRO_FILTRO_IA_RECUSADO =
  'A sugestão da IA foi recusada pela validação de segurança e não pode ser ' +
  'aplicada. Reescreva o pedido usando critérios de carteira (PL, ROA, status, ' +
  'região).'

// -----------------------------------------------------------------------
// Assistente do rascunho da simulação (spec-014, US-2 e US-3)
// -----------------------------------------------------------------------

// O ícone ao lado de "Guardar cenário" é ATALHO para o mesmo agente do módulo,
// não um segundo assistente (invariante da spec-016). O rótulo diz isso: o
// consultor não pode ficar decidindo a qual dos dois perguntar.
export const ASSISTENTE_SIMULACAO_LABELS = {
  botao: 'Analisar este cenário com o Especialista em Fee Fixo',
  ajuda:
    'Abre o mesmo Especialista em Fee Fixo do módulo, já com as premissas e ' +
    'os resultados desta tela — sem precisar guardar o cenário antes.',
  // ★ Sem a flag `IA - Fee Fixo` o atalho fica visível e desabilitado — este é
  // o texto do tooltip nesse estado (o normal, `ajuda`, some junto do acesso).
  ajudaSemAcesso:
    'Disponível no assistente de IA do Fee Fixo. Fale com o time AAWZ para ' +
    'assinar e liberar este atalho.',
  perguntaFavoravel:
    'Explique este cenário da simulação que estou montando: o que os números ' +
    'mostram e quais premissas mais pesam no resultado?',
  // Em cenário ruim a pergunta é a do print do owner. Ela não pede argumento de
  // venda: pede a causa — que é o que a US-4 exige que o agente responda.
  perguntaDesfavoravel:
    'Por que a economia do cliente está negativa neste cenário, e o que ' +
    'mudaria o resultado?',
}

export const SUGESTAO_RASCUNHO_LABELS = {
  titulo: 'Ajustes sugeridos',
  descricao:
    'O assistente recomenda; quem aplica é você. Cada ajuste move um controle ' +
    'da simulação e pode ser desfeito.',
  descricaoDesfavoravel:
    'Nestas premissas o cenário não favorece o cliente. Estes ajustes mudam o ' +
    'resultado na direção certa — aplique se fizerem sentido para este caso.',
  aplicar: 'Aplicar',
  desfazer: 'Desfazer',
  descartar: 'Descartar',
  fechar: 'Fechar',
  aplicadoAgora:
    'Ajustes aplicados ao cenário ativo. Os números da tela já foram ' +
    'recalculados pelo laboratório.',
}

// A recusa da sugestão é da tela para o consultor: ele não escolheu o valor e
// não tem o que corrigir. A faixa do controle vai na mensagem do util — sem
// ela, "valor inválido" não diz o que fazer em seguida.
export const ERRO_SUGESTAO_RASCUNHO_RECUSADA =
  'Não foi possível aplicar a sugestão do assistente. Ajuste o controle à mão ' +
  'no painel de premissas.'

const CONECTIVO_POR_OPERADOR = {
  '>=': 'a partir de',
  '<=': 'até',
  '>': 'acima de',
  '<': 'abaixo de',
  '!=': 'diferente de',
}

// Rótulo legível dos enums, na MESMA fonte que a tabela de clientes usa: se o
// status mudar de nome na tabela, muda junto aqui.
const rotuloDoValor = (campo, valor) => {
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não'
  if (campo === 'status_migracao') {
    const config = STATUS_MIGRACAO_CONFIG[valor]
    return config ? config.label : String(valor)
  }
  if (campo === 'status_elegibilidade') {
    return STATUS_LABELS[valor] || String(valor)
  }
  return String(valor)
}

// O número só é legível com a unidade que o util declara: `0.005` é "0,5% a.a."
// e `1.0` é "100%" — sem isso, os dois são igualmente plausíveis para quem lê.
const valorNumericoEmTexto = (unidade, valor) => {
  if (unidade === 'reais') return toMoney(valor, 0)
  if (unidade === 'fracao_ao_ano')
    return `${toPercent(valor * 100, 2, true)} a.a.`
  if (unidade === 'fracao') return toPercent(valor * 100, 2, true)
  if (unidade === 'anos') return `${valor} anos`
  return String(valor)
}

const valorEmTexto = (campo, meta, valor) => {
  if (Array.isArray(valor)) {
    return valor.map((item) => valorEmTexto(campo, meta, item)).join(' ou ')
  }
  if (meta.tipo === 'numero') return valorNumericoEmTexto(meta.unidade, valor)
  return rotuloDoValor(campo, valor)
}

/**
 * Traduz o filtro tipado para as frases que o consultor lê no preview.
 *
 * ★ O mapa de campos entra por PARÂMETRO, e não por import: este arquivo é
 * folha da árvore de dependências do módulo (`feeLab.js` e vários componentes
 * leem daqui). Importar o util criaria o ciclo
 * `filtro-tipado → feeLab → Text → filtro-tipado`, e ciclo em ESM entrega
 * `undefined` para quem for avaliado primeiro — falha calada, na carga.
 *
 * @param {Array<Object>} filtros Filtros já validados.
 * @param {Object} campos Metadados dos campos (`CAMPOS_FILTRAVEIS` do util):
 *   é de onde saem o rótulo e a unidade de cada valor.
 * @returns {Array<string>} Uma frase por filtro, na ordem em que vieram.
 */
export function montarDescricaoFiltro(filtros, campos = {}) {
  if (!Array.isArray(filtros)) return []

  return filtros.map((filtro) => {
    const meta = campos[filtro.campo]
    // Campo desconhecido não chega aqui (o util recusa antes), mas a frase não
    // pode quebrar a tela se chegar: dizer o campo cru é melhor que sumir.
    if (!meta) return `${filtro.campo} ${filtro.operador} ${filtro.valor}`

    const valor = valorEmTexto(filtro.campo, meta, filtro.valor)
    const conectivo = CONECTIVO_POR_OPERADOR[filtro.operador]
    if (conectivo) return `${meta.rotulo} ${conectivo} ${valor}`
    // `==` e `in` viram "rótulo: valor" — "status da migração igual a Não
    // Iniciado" é como robô falando.
    return `${meta.rotulo}: ${valor}`
  })
}

// Parte da base sem carteira: o filtro roda em quem tem, e o número de fora vai
// escrito. Sem ele, o consultor leria o recorte como se fosse a base inteira.
export function montarAvisoClasseSemDado({ quantos } = {}) {
  if (!quantos) return ''
  const sujeito =
    quantos === 1
      ? '1 cliente da visão não tem a carteira classificada'
      : `${quantos} clientes da visão não têm a carteira classificada`
  return (
    `${sujeito} e ficaram de fora deste recorte — eles não foram contados como ` +
    '0% na classe, apenas não há o dado.'
  )
}
